import type {
  Basic,
  Meta,
  Reference,
  CodeableConcept,
  Coding,
  Identifier,
  Extension,
} from 'fhir/r4';

import { useFhirStore } from '@/stores/fhirStore';
import userManager from '@/helperScripts/userManagement/userManager';
import { useUserStore } from '@/stores/userStore';
import ReferenceClass from './Reference';

export default class SearchQueryStore implements Basic {
  /** Resource Type Name (for serialization) */
  readonly resourceType: 'Basic';
  /**
   * Indicates who was responsible for creating the resource instance.
   */
  author?: Reference | undefined;
  /**
   * Because resource references will only be able to indicate 'Basic', the type of reference will need to be specified in a Profile identified as part of the resource.  Refer to the resource notes section for information on appropriate terminologies for this code.
   * This element is labeled as a modifier because it defines the meaning of the resource and cannot be ignored.
   */
  code: CodeableConcept;
  /**
   * Identifies when the resource was first created.
   */
  created?: string | undefined;
  _created?: Element | undefined;
  /**
   * Identifier assigned to the resource for business purposes, outside the context of FHIR.
   */
  identifier?: Identifier[] | undefined;
  /**
   * Optional as not all potential resources will have subjects.  Resources associated with multiple subjects can handle this via extension.
   */
  subject?: Reference;

  meta: Meta;

  extension?: SearchQueryParentExtension[] | undefined;

  constructor(practitionerId: string) {
    this.meta = new SearchQueryStoreMeta();
    this.code = new SearchQueryStoreCodeableConcept();
    this.subject = new ReferenceClass(
      userManager.getFhirResourceTypeForUserRole(useUserStore().usrRole) +
        '/' +
        practitionerId
    );
    this.resourceType = 'Basic';
    this.extension = new Array<SearchQueryParentExtension>();
  }
}

class SearchQueryStoreMeta implements Meta {
  profile?: string[] | undefined;
  constructor() {
    this.profile = [useFhirStore().urls.searchQueryStoreProfileUrl];
  }
}

class SearchQueryStoreCodeableConcept implements CodeableConcept {
  coding: Array<SearchQueryStoreCoding>;
  constructor() {
    this.coding = [new SearchQueryStoreCoding()];
  }
}

class SearchQueryStoreCoding implements Coding {
  system: string;
  display: string;
  code: string;
  constructor() {
    this.code = useFhirStore().basicResourceSearchQueryCode;
    this.display = useFhirStore().basicResourceSearchQueryDisplay;
    this.system = useFhirStore().urls.basicResourceCodesCsUrl;
  }
}

export class SearchQueryParentExtension implements Extension {
  url: string;
  extension?: Array<SearchQueryNameExtension | SearchQueryQueryExtension>;
  constructor(query: string, name: string) {
    this.url = useFhirStore().urls.searchQueryExtensionUrl;
    this.extension = [
      new SearchQueryNameExtension(name),
      new SearchQueryQueryExtension(query),
    ];
  }
}

class SearchQueryNameExtension implements Extension {
  valueString: string;
  url: string;
  constructor(name: string) {
    this.url = useFhirStore().urls.searchQueryExtensionNameUrl;
    this.valueString = name;
  }
}

class SearchQueryQueryExtension implements Extension {
  valueString: string;
  url: string;
  constructor(query: string) {
    this.url = useFhirStore().urls.searchQueryExtensionQueryUrl;
    this.valueString = query;
  }
}
